import { useState } from 'react';

/**
 * 「クリックでクリップボードコピーできる」トークン表示。
 *
 * 放送地点マスタの「？ 利用できるプレースホルダ」ヘルプで使っていたものを共通化した部品。
 * SSML 辞書マスタでは、アクセント記号「'」のコピーボタンとしても使う。
 *
 * - `<code>` 風の見た目を維持しつつ `<button>` 要素として描画する。
 * - クリックすると `navigator.clipboard.writeText` でトークン文字列をコピーし、
 *   一時的に「コピー済み」を表示する。
 * - クリップボード API が使えない環境 (古いブラウザ / セキュア コンテキスト外) では
 *   そっとフォールバックして何もしない (テキスト選択は可能)。
 */
export function CopyableToken({
  token,
  testId,
}: {
  token: string;
  /** テスト用の `data-testid` (省略可) */
  testId?: string;
}): React.JSX.Element {
  const [copied, setCopied] = useState(false);
  const handleCopy = (): void => {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(token).then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1200);
      });
    }
  };
  return (
    <button
      type="button"
      className="copyable-token"
      onClick={handleCopy}
      title={`${token} をコピー`}
      aria-label={`${token} をクリップボードにコピー`}
      data-testid={testId}
      style={{
        font: 'inherit',
        fontFamily:
          'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
        background: '#f4f6f8',
        border: '1px solid #d0d7de',
        borderRadius: 4,
        padding: '1px 6px',
        cursor: 'pointer',
        color: '#24292f',
        whiteSpace: 'nowrap',
      }}
    >
      <code style={{ background: 'transparent', padding: 0 }}>{token}</code>
      {copied ? (
        <span aria-live="polite" style={{ marginLeft: 4, fontSize: '0.85em', color: '#0969da' }}>
          ✓ コピー済み
        </span>
      ) : null}
    </button>
  );
}
